import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { getIntegrationStatus, postIntegrationStatus } from '@api/integrationTool';
import { useFeature } from '@src/globalStore/featureStore';
import featureNames from '@utils/featureToggle/constants';
import { useDataBridgeStore } from '@pulse/nav';

const { baseUrl } = window.conviva;
const ECO_INTEGRATION_STATUS_QUERY_KEY = 'eco-integration-status';

export default function useSelfServeIntegrationStatus() {
  const navigate = useNavigate();
  const selfServeFF = useFeature(featureNames.SELF_SERVE);
  const selfServeIntegrationStatusFromParameterTable = useFeature(
    featureNames.SELF_SERVE_INTEGRATION_STATUS,
  );

  const { isFetched: isIntegrationStatusFetched } = useQuery(
    [ECO_INTEGRATION_STATUS_QUERY_KEY],
    async () => {
      const res = await getIntegrationStatus({});
      const integrationStatus = !!res?.data?.integrationStatus;
      if (
        !selfServeIntegrationStatusFromParameterTable &&
        selfServeFF &&
        integrationStatus === false
      ) {
        navigate('/app/app-insights/welcome', { replace: true });
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: selfServeFF && !selfServeIntegrationStatusFromParameterTable,
      staleTime: Infinity,
      notifyOnChangeProps: ['isError', 'isFetched', 'data'],
    },
  );

  return {
    isFetched:
      !selfServeFF || isIntegrationStatusFetched || selfServeIntegrationStatusFromParameterTable,
  };
}

export function useUpdateIntegrationStatus() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setDataBridgeMap } = useDataBridgeStore();

  return useMutation(
    (payload: { integrationStatus: boolean }) => {
      return postIntegrationStatus(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ECO_INTEGRATION_STATUS_QUERY_KEY]);
        navigate(`${baseUrl}/dashboard/app-experience`, { replace: true });
        // notify pulse-nav to clear the cache
        setDataBridgeMap({ integrationStatus: true });
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update integration status');
      },
    },
  );
}
